import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ProductRow, blobErrorMessage } from "../../../../services/v2/inventory.service";
import ReasonPrompt from "../../../../components/kits/reasonPrompt";
import { Empty, Loading, PER_PAGE_DEFAULT, Pager, apiError, saveBlob, ui, useCampus, useDebounced, useIdleLocked, usePerms } from "../kits/shared";
import CatalogoViejo from "./catalogoViejo";
import FichaProducto from "./fichaProducto";
import ProductForm from "./productForm";
import ProductPicker from "./productPicker";
import { VariantesModal } from "./variantes";
import { useSelector } from "react-redux";
import { RootState } from "../../../../globals";
import { BarraSeleccion, Buscador, MenuMas, Segmento, Seleccionado, VentanaTienda } from "./piezasTienda";
import { UnificarVentana } from "./unificarProductos";

/* Tienda → Productos (sección "products"): catálogo, kits y uniformes compuestos, tallas y kits por curso. */

type TabKey = "kit" | "uniform" | "article" | "academic" | "digital" | "service" | "courses" | "prices" | "legacy";
const TABS: { key: TabKey; label: string }[] = [
	{ key: "kit", label: "Kits" },
	{ key: "uniform", label: "Uniformes" },
	{ key: "article", label: "Artículos" },
	{ key: "academic", label: "Productos Académicos" },
	{ key: "digital", label: "Digitales" },
	{ key: "service", label: "Servicios y cobros" },
	{ key: "courses", label: "Kits por Curso" },
	{ key: "prices", label: "Precios por aprobar" },
	{ key: "legacy", label: "Catálogo viejo" }
];
const SECTION = "products";

/** Miniatura de la foto del producto (viaja autenticada, por eso se descarga como blob). */
const Thumb: React.FC<{ campusId: number; productId: number; has: boolean }> = ({ campusId, productId, has }) => {
	const [url, setUrl] = useState<string | null>(null);
	/*
		Solo cuando la miniatura se ve (29-oct-2026): la lista pedía las 250 fotos de la página de golpe y gastaba el tope de
		peticiones por minuto de la sesión ("Demasiados intentos"). Empieza a cargar 200 px antes de entrar a la pantalla.
	*/
	const caja = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState<boolean>(false);
	useEffect(() => {
		if (!has || visible) return;
		const el = caja.current;
		if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
		const obs = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { setVisible(true); obs.disconnect(); } }, { rootMargin: "200px" });
		obs.observe(el);
		return () => obs.disconnect();
	}, [has, visible]);
	useEffect(() => {
		if (!has || !visible) return;
		let objectUrl: string | null = null;
		let alive = true;
		// Miniatura de 160 px: la foto original de la cámara pesa varios MB y la lista trae 250 por página.
		HttpManager.getInstance().inventoryService.photoBlob(campusId, productId, true)
			.then((response) => { if (!alive) return; objectUrl = URL.createObjectURL(response.data); setUrl(objectUrl); })
			.catch(() => undefined);
		return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
	}, [campusId, productId, has, visible]);
	return <div ref={caja} className="wti-thumb">
		{url ? <img src={url} alt="" /> : <span>sin foto</span>}
	</div>;
};

const Productos: React.FC = () => {
	const { campus, campusId, currency } = useCampus();
	const idleLocked = useIdleLocked();
	const perms: any = usePerms(SECTION);
	const canCreate = !!perms.create;
	// Editar, Tallas y Baja/Reactivar son POST en el back: piden el bit Crear, no Actualizar. Kits por Curso es PUT: Actualizar.
	const canEdit = !!perms.create;
	const canUpdate = !!perms.update;
	const http = HttpManager.getInstance();

	const [tabKey, setTabKey] = useState<TabKey>("kit");
	const [catalogs, setCatalogs] = useState<any>(null);
	const [query, setQuery] = useState<string>("");
	const debouncedQuery = useDebounced(query);
	const [includeDeprecated, setIncludeDeprecated] = useState<boolean>(false);
	const [rows, setRows] = useState<ProductRow[]>([]);
	const [courseRows, setCourseRows] = useState<any[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [page, setPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	/** Aviso arriba de la tabla (no la reemplaza): fallas al abrir un producto, al exportar, o lo que avisó el back. */
	const [notice, setNotice] = useState<{ text: string; bad: boolean } | null>(null);
	const [exporting, setExporting] = useState<boolean>(false);
	const requestRef = useRef<number>(0);
	const [counts, setCounts] = useState<Record<string, number>>({});

	const [formProduct, setFormProduct] = useState<any | null | undefined>(undefined); // undefined = cerrado, null = alta
	const [sizesProduct, setSizesProduct] = useState<any | null>(null);
	const [deprecating, setDeprecating] = useState<ProductRow | null>(null);
	const [soldOutRow, setSoldOutRow] = useState<ProductRow | null>(null);
	const [modalBusy, setModalBusy] = useState<boolean>(false);
	const [modalError, setModalError] = useState<string | null>(null);
	/** Ficha grande (foto ampliable, cambiar nombre, existencia por bodega): el id del producto abierto. */
	const [detailId, setDetailId] = useState<number | null>(null);
	// Cuadritos para unificar repetidos (Super Admin). Lo marcado se queda aunque cambie la búsqueda, la página o la pestaña.
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const isSuperAdmin = currentUser?.currentRole === "superAdmin";
	const [selected, setSelected] = useState<Seleccionado[]>([]);
	const [merging, setMerging] = useState<boolean>(false);
	const toggleSelected = (row: ProductRow) => setSelected((list) => list.some((item) => item.id === row.id) ? list.filter((item) => item.id !== row.id)
		: [...list, { id: row.id, name: row.name, waCode: row.waCode, kind: row.kind, onHand: Number(row.onHand || 0), variantCount: Number(row.variantCount || 0), modelId: row.modelId || null }]);
	const [courseEditing, setCourseEditing] = useState<any | null>(null);

	useEffect(() => {
		if (!campusId) return;
		http.inventoryService.catalogs(campusId).then((response) => setCatalogs(response.data)).catch((requestError) => {
			setCatalogs(null);
			setNotice({ text: apiError(requestError, "No se pudieron cargar los catálogos (tipos, tallas, cursos)."), bad: true });
		});
	}, [campusId]);

	const load = useCallback(async () => {
		if (!campusId) return;
		// Solo la última petición pinta: al cambiar rápido de pestaña, una respuesta lenta de Kits no cae bajo Artículos.
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		try {
			if (tabKey === "prices" || tabKey === "legacy") {
				setLoading(false);
				return;
			}
			if (tabKey === "courses") {
				const response = await http.inventoryService.courseKits(campusId);
				if (request !== requestRef.current) return;
				setCourseRows(response.data || []);
				setTotal((response.data || []).length);
				setLastPage(1);
			} else {
				const response = await http.inventoryService.list(campusId, { kinds: [tabKey], q: debouncedQuery.trim(), includeDeprecated: includeDeprecated ? 1 : 0 }, page, PER_PAGE_DEFAULT);
				if (request !== requestRef.current) return;
				setRows(response.data.data || []);
				setTotal(response.data.total || 0);
				setLastPage(response.data.last_page || 1);
			}
		} catch (requestError: any) {
			if (request !== requestRef.current) return;
			setError(apiError(requestError, "No se pudo cargar el catálogo."));
			setRows([]);
		} finally {
			if (request === requestRef.current) setLoading(false);
		}
	}, [campusId, tabKey, debouncedQuery, includeDeprecated, page]);

	useEffect(() => { load(); }, [load]);

	const refreshCounts = useCallback(() => {
		if (!campusId) return;
		TABS.filter((tab) => tab.key !== "courses" && tab.key !== "prices" && tab.key !== "legacy").forEach((tab) => {
			http.inventoryService.list(campusId, { kinds: [tab.key] }, 1, 1)
				.then((response) => setCounts((previous) => ({ ...previous, [tab.key]: response.data.total || 0 })))
				.catch(() => undefined);
		});
	}, [campusId]);
	useEffect(() => { refreshCounts(); }, [refreshCounts]);

	const after = async () => { await load(); refreshCounts(); };

	const openEdit = async (row: ProductRow) => {
		setModalError(null);
		setNotice(null);
		try {
			const response = await http.inventoryService.get(campusId, row.id);
			setFormProduct(response.data);
		} catch (requestError) {
			setNotice({ text: apiError(requestError, "No se pudo abrir el producto."), bad: true });
		}
	};

	/** Variantes: tallas, colores y precio por variante (se abre con lo que ya existe). */
	const openSizes = async (row: ProductRow) => {
		setModalError(null);
		setNotice(null);
		try {
			const response = await http.inventoryService.get(campusId, row.id);
			setSizesProduct(response.data);
		} catch (requestError) {
			setNotice({ text: apiError(requestError, "No se pudo abrir el producto."), bad: true });
		}
	};

	const exportExcel = async () => {
		setExporting(true);
		setNotice(null);
		try {
			const response = await http.stockOpsService.exportProducts(campusId, { kinds: [tabKey], q: debouncedQuery.trim(), includeDeprecated: includeDeprecated ? 1 : 0 });
			saveBlob(response.data, "productos.xlsx", "Inventario");
		} catch (requestError) {
			setNotice({ text: await blobErrorMessage(requestError, "No se pudo generar el Excel."), bad: true });
		} finally {
			setExporting(false);
		}
	};

	const saveProduct = async (data: Record<string, any>, photo: File | null) => {
		setModalBusy(true);
		setModalError(null);
		try {
			const { newSizes, newColors, existingColor, existingSize, ...fields } = data;
			const response = formProduct ? await http.inventoryService.update(campusId, formProduct.id, fields, photo) : await http.inventoryService.create(campusId, fields, photo);
			const productId = formProduct ? formProduct.id : response?.data?.product?.id;
			if (((Array.isArray(newSizes) && newSizes.length > 0) || (Array.isArray(newColors) && newColors.length > 0)) && productId) {
				try {
					await http.inventoryService.createVariants(campusId, productId, newSizes || [], newColors || [], existingColor, existingSize);
				} catch (sizesError: any) {
					// The product is saved; say why the variants were not, and let them retry from "Variantes".
					setFormProduct(undefined);
					setNotice({ text: "Se guardó el producto, pero no las tallas y colores: " + apiError(sizesError, "intenta con el botón Variantes."), bad: true });
					await after();
					return;
				}
			}
			setFormProduct(undefined);
			// Mismo nombre que otro producto: el back lo guarda como "Nombre (2)" y aquí se dice cómo quedó.
			const savedName = response?.data?.product?.name;
			const typedName = String(fields.name || "").trim().replace(/\s+/g, " ");
			if (savedName && typedName && savedName !== typedName) setNotice({ text: "Ya había otro producto llamado «" + typedName + "»; este quedó como «" + savedName + "».", bad: false });
			await after();
		} catch (requestError: any) {
			setModalError(apiError(requestError, "No se pudo guardar."));
		} finally {
			setModalBusy(false);
		}
	};

	const onDeprecate = async (reason: string) => {
		if (!deprecating) return;
		setModalBusy(true);
		setModalError(null);
		try {
			if (deprecating.deprecatedAt) await http.inventoryService.restore(campusId, deprecating.id, reason);
			else await http.inventoryService.deprecate(campusId, deprecating.id, reason);
			setDeprecating(null);
			await after();
		} catch (requestError: any) {
			setModalError(apiError(requestError, "No se pudo completar la operación."));
		} finally {
			setModalBusy(false);
		}
	};

	const onSoldOut = async (reason: string) => {
		if (!soldOutRow) return;
		setModalBusy(true);
		setModalError(null);
		try {
			await http.inventoryService.soldOut(campusId, soldOutRow.id, !soldOutRow.soldOutAt, reason);
			setSoldOutRow(null);
			await after();
		} catch (requestError: any) {
			setModalError(apiError(requestError, "No se pudo completar la operación."));
		} finally {
			setModalBusy(false);
		}
	};

	const openDetail = (row: ProductRow) => { setNotice(null); setDetailId(row.id); };

	// ---------------------------------------------------------------- tablas

	const productsTable = () => <table>
		<thead><tr>
			{isSuperAdmin ? <th className="wti-chk"><span className="visually-hidden">Seleccionar</span></th> : null}
			<th style={{ width: 56 }}></th>
			<th>Producto</th>
			<th className="der">Precio</th>
			{tabKey === "kit" || tabKey === "uniform" ? <th>Versión</th> : null}
			{tabKey === "article" ? <th className="der">Existencia</th> : null}
			<th>Variantes</th>
			<th></th>
		</tr></thead>
		<tbody>{rows.map((row) => {
			const isSel = selected.some((item) => item.id === row.id);
			return <tr key={row.id} className={"wti-fila" + (isSel ? " sel" : "")} style={{ opacity: row.deprecatedAt ? 0.6 : 1 }}>
				{isSuperAdmin ? <td className="wti-chk"><input type="checkbox" checked={isSel} disabled={idleLocked} aria-label={"Seleccionar " + row.name} onChange={() => toggleSelected(row)} /></td> : null}
				<td onClick={() => openDetail(row)} title="Ver ficha"><Thumb campusId={campusId} productId={row.id} has={row.hasPhoto} /></td>
				<td>
					<button type="button" className="wti-nombre" title="Ver ficha" onClick={() => openDetail(row)}>{row.name}</button>
					<div className="wti-sub">{row.waCode || "sin código"}{row.barcode ? " · " + row.barcode : ""}{row.deprecatedAt ? " · dado de baja el " + row.deprecatedAt : ""}
						{row.soldOutAt && !row.deprecatedAt ? <span className="waAgotado" title={"Agotado desde el " + row.soldOutAt}>Agotado</span> : null}</div>
				</td>
				<td className="der wau-num" style={{ whiteSpace: "nowrap" }}>
					{row.pricePending ? <span className="wau-oro" style={{ fontWeight: 600 }}>Sin precio</span> : <>{currency}{row.price.toFixed(2)}</>}
					{row.suggestedPrice ? <div className="wti-sub">sugerido {currency}{Number(row.suggestedPrice).toFixed(2)}</div> : null}
				</td>
				{tabKey === "kit" || tabKey === "uniform" ? <td>
					{row.versionLabel ? <>{row.versionLabel}{row.tier ? <div className="wti-sub">{row.tier}</div> : null}</> : <span className="wau-oro">Sin piezas</span>}
				</td> : null}
				{tabKey === "article" ? <td className="der wau-num">{row.trackStock ? (row.onHand ? row.onHand : <span className="wti-cero">–</span>) : <span className="wti-sub">por variante</span>}</td> : null}
				<td>{row.variantCount > 0 ? row.variantCount : (row.sizeType && row.sizeType !== "none" ? <span className="wti-sub">sin crear</span> : <span className="wti-cero">–</span>)}</td>
				<td><div className="wti-acciones">
					{canEdit ? <button type="button" className="wau-btn s mini" disabled={idleLocked} onClick={() => openEdit(row)}>Editar</button> : null}
					<MenuMas disabled={idleLocked} opciones={[
						row.kind === "article" && !row.modelId && (canEdit || row.variantCount > 0) ? { label: "Tallas y Colores", onClick: () => openSizes(row) } : null,
						canEdit && !row.deprecatedAt && !row.modelId ? { label: row.soldOutAt ? "Volver a Vender" : "Marcar Agotado", onClick: () => { setModalError(null); setSoldOutRow(row); } } : null,
						canEdit ? { label: row.deprecatedAt ? "Reactivar" : "Dar de Baja", peligro: !row.deprecatedAt, onClick: () => { setModalError(null); setDeprecating(row); } } : null
					]} />
				</div></td>
			</tr>;
		})}</tbody>
	</table>;

	const coursesTable = () => <table>
		<thead><tr>
			<th>Curso</th>
			<th>Kits que se ofrecen</th>
			<th></th>
		</tr></thead>
		<tbody>{courseRows.map((course) => <tr key={course.courseId}>
			<td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{course.courseBrief}</td>
			<td>
				{course.kits.length < 1 ? <span className="wti-sub">Sin kit asignado</span>
					: course.kits.map((kit: any, index: number) => <span key={kit.kitArticleId} className={"wau-pastilla" + (kit.required ? " acento" : "")} style={{ marginRight: 6, marginBottom: 4 }}>
						{index + 1}. {kit.name} · {kit.pricePending ? "sin precio" : currency + Number(kit.price || 0).toFixed(2)}{kit.required ? "" : " · recomendado"}
					</span>)}
			</td>
			<td className="der">
				{canUpdate ? <button type="button" className="wau-btn s mini" disabled={idleLocked} onClick={() => { setModalError(null); setCourseEditing({ ...course, draft: [...course.kits] }); }}>Editar</button> : null}
			</td>
		</tr>)}</tbody>
	</table>;

	if (!campusId) return <div className="wau">Selecciona un plantel.</div>;
	const listTab = tabKey !== "courses" && tabKey !== "prices" && tabKey !== "legacy";

	return <div className="wau wti">
		<div className="wau-cab">
			<h2>Productos</h2>
			{canCreate && listTab ? <button type="button" className="wau-btn p" disabled={idleLocked} onClick={() => { setModalError(null); setFormProduct(null); }}>Nuevo Producto</button> : null}
		</div>
		<div className="wau-tabs" role="tablist">
			{TABS.filter((tab) => tab.key !== "prices" || !!catalogs?.priceRole).map((tab) => <button key={tab.key} type="button" role="tab" className="wau-tab" aria-selected={tab.key === tabKey} disabled={idleLocked}
				onClick={() => { setTabKey(tab.key); setPage(1); setRows([]); }}>
				{tab.key === "prices" ? (catalogs?.priceRole === "review" ? "Precios por Revisar" : "Precios por Aprobar") : tab.label}
				{tab.key === "prices" ? (catalogs?.pendingPrices ? <small className="oro">{catalogs.pendingPrices}</small> : null)
					: counts[tab.key] !== undefined && tab.key !== "courses" ? <small>{counts[tab.key]}</small> : null}
			</button>)}
		</div>

		{tabKey === "legacy" ? <CatalogoViejo campusId={campusId} currency={currency} canEdit={!!canCreate} idleLocked={idleLocked} /> : null}
		{listTab ? <div className="wti-filtros">
			<Buscador value={query} disabled={idleLocked} placeholder="Nombre, código WA, SKU o escanear código de barras" onChange={(value) => { setQuery(value); setPage(1); }} />
			<Segmento opciones={[["0", "Activos"], ["1", "Con Bajas"]]} value={includeDeprecated ? "1" : "0"} disabled={idleLocked} onChange={(value) => { setIncludeDeprecated(value === "1"); setPage(1); }} />
			<button type="button" className="wau-btn s mini" disabled={idleLocked || exporting} onClick={exportExcel}>{exporting ? "Generando…" : "Exportar Excel"}</button>
		</div> : null}
		{notice ? <div className={"wau-aviso" + (notice.bad ? "" : " ok")} style={notice.bad ? { color: "var(--wa-oro)" } : undefined}>
			<span>{notice.text}</span><button type="button" aria-label="Cerrar aviso" onClick={() => setNotice(null)}>×</button>
		</div> : null}

		{listTab ? <Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} /> : null}
		{tabKey !== "legacy" ? <div className="wau-tabla">
			{loading ? <Loading /> : error ? <div className="wau-aviso" style={{ color: "var(--wa-oro)" }}>{error}</div>
				: tabKey === "prices" ? <PriceTray campusId={campusId} currency={currency} review={catalogs?.priceRole === "review"} onChanged={() => { after(); http.inventoryService.catalogs(campusId).then((response) => setCatalogs(response.data)).catch(() => undefined); }} />
				: tabKey === "courses" ? (courseRows.length ? coursesTable() : <Empty text="No hay cursos en este plantel." />)
				: rows.length ? productsTable() : <Empty text="Sin productos en esta categoría." />}
		</div> : null}
		{listTab && lastPage > 1 ? <Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} /> : null}

		{isSuperAdmin ? <BarraSeleccion items={selected} onQuitar={(id) => setSelected(selected.filter((item) => item.id !== id))} onLimpiar={() => setSelected([])}>
			<button type="button" className={"wau-btn p" + (selected.length < 2 ? " wti-apagado" : "")} disabled={idleLocked}
				title={selected.length < 2 ? "Marca al menos 2 productos que sean el mismo." : undefined}
				onClick={() => { if (selected.length < 2) { setNotice({ text: "Para unificar, marca al menos 2 productos que sean el mismo.", bad: true }); return; } setMerging(true); }}>
				Unificar{selected.length > 1 ? " " + selected.length : ""}
			</button>
		</BarraSeleccion> : null}
		{merging ? <UnificarVentana campusId={campusId} iniciales={selected} onClose={() => setMerging(false)}
			onDone={(message, survivorId) => { setMerging(false); setSelected([]); setNotice({ text: message, bad: false }); after(); setDetailId(survivorId); }} /> : null}

		{formProduct !== undefined ? <ProductForm campusId={campusId} catalogs={catalogs} product={formProduct} defaultKind={tabKey === "courses" ? "kit" : tabKey}
			busy={modalBusy} error={modalError} onSave={saveProduct} onCancel={() => { if (!modalBusy) setFormProduct(undefined); }} /> : null}

		{sizesProduct ? <VariantesModal campusId={campusId} catalogs={catalogs} product={sizesProduct} currency={currency} canEdit={canEdit}
			onChanged={() => { after(); http.inventoryService.catalogs(campusId).then((response) => setCatalogs(response.data)).catch(() => undefined); }}
			onClose={() => setSizesProduct(null)} /> : null}

		{deprecating ? <ReasonPrompt
			title={deprecating.deprecatedAt ? "Reactivar producto" : "Dar de baja el producto"}
			description={deprecating.deprecatedAt
				? <><b>{deprecating.name}</b> volverá al catálogo y podrá venderse otra vez.</>
				: <><b>{deprecating.name}</b> sale del catálogo. No se borra: su historial y sus ventas anteriores se conservan.</>}
			confirmLabel={deprecating.deprecatedAt ? "Reactivar" : "Dar de baja"}
			confirmClass={deprecating.deprecatedAt ? "btn-primary" : "btn-warning"}
			busy={modalBusy} error={modalError} onConfirm={onDeprecate} onCancel={() => { if (!modalBusy) setDeprecating(null); }} /> : null}

		{soldOutRow ? <ReasonPrompt
			title={soldOutRow.soldOutAt ? "Volver a vender" : "Marcar como agotado"}
			description={soldOutRow.soldOutAt
				? <><b>{soldOutRow.name}</b> se vuelve a vender en Nueva Venta, Estado de Cuenta y el botón Kits de WappChat.</>
				: <><b>{soldOutRow.name}</b> deja de venderse hasta que lo vuelvas a poner a la venta. No se borra nada.</>}
			confirmLabel={soldOutRow.soldOutAt ? "Volver a Vender" : "Marcar Agotado"}
			confirmClass={soldOutRow.soldOutAt ? "btn-primary" : "btn-warning"}
			busy={modalBusy} error={modalError} onConfirm={onSoldOut} onCancel={() => { if (!modalBusy) setSoldOutRow(null); }} /> : null}

		{detailId !== null ? <FichaProducto campusId={campusId} productId={detailId} currency={currency} canRename={canEdit}
			onRenamed={() => { load(); }} onClose={() => setDetailId(null)} /> : null}

		{courseEditing ? <CourseKitsModal campusId={campusId} course={courseEditing} currency={currency} busy={modalBusy} error={modalError}
			onSave={async (kits) => {
				setModalBusy(true);
				setModalError(null);
				try {
					await http.inventoryService.saveCourseKits(campusId, courseEditing.courseId, kits);
					setCourseEditing(null);
					await load();
				} catch (requestError: any) {
					setModalError(apiError(requestError, "No se pudo guardar."));
				} finally { setModalBusy(false); }
			}}
			onCancel={() => { if (!modalBusy) setCourseEditing(null); }} /> : null}
	</div>;
};

// ------------------------------------------------------------------ modales auxiliares

const CourseKitsModal: React.FC<{ campusId: number; course: any; currency: string; busy?: boolean; error?: string | null; onSave: (kits: any[]) => void; onCancel: () => void }> = ({ campusId, course, currency, busy, error, onSave, onCancel }) => {
	const [kits, setKits] = useState<any[]>(course.draft || []);
	const pie = <>
		<button type="button" className="wau-btn s" disabled={busy} onClick={onCancel}>Cancelar</button>
		<button type="button" className="wau-btn p" disabled={busy} onClick={() => onSave(kits.map((kit, index) => ({ ...kit, tierRank: index + 1 })))}>{busy ? "Guardando…" : "Guardar"}</button>
	</>;
	return <VentanaTienda titulo={"Kits de " + course.courseBrief} onHide={onCancel} busy={busy} pie={pie}>
		<div style={{ marginBottom: 12 }}>
			<ProductPicker campusId={campusId} kinds={["kit", "uniform"]} disabled={busy} placeholder="Agregar kit…" excludeIds={kits.map((k) => k.kitArticleId)}
				onPick={(picked) => setKits([...kits, { kitArticleId: picked.id, name: picked.name, price: picked.price, pricePending: !!picked.pricePending, required: kits.length === 0, tierRank: kits.length + 1, fromLevelNumber: null }])} />
		</div>
		{kits.length < 1 ? <div className="wti-sub">Sin kits asignados.</div>
			: kits.map((kit, index) => <div key={kit.kitArticleId} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--wa-filo)", flexWrap: "wrap" }}>
				<span style={{ fontWeight: 600, flex: "1 1 180px" }}>{index + 1}. {kit.name} <span className={kit.pricePending ? "wti-sub wau-oro" : "wti-sub"}>{kit.pricePending ? "sin precio" : currency + Number(kit.price || 0).toFixed(2)}</span></span>
				<select className="wau-entrada" style={{ width: 150 }} value={kit.required ? "1" : "0"} disabled={busy}
					onChange={(event) => setKits(kits.map((k, i) => i === index ? { ...k, required: event.target.value === "1" } : k))}>
					<option value="1">Obligatorio</option>
					<option value="0">Recomendado</option>
				</select>
				<input type="number" min={1} className="wau-entrada" style={{ width: 130 }} placeholder="Desde módulo" value={kit.fromLevelNumber ?? ""} disabled={busy}
					onChange={(event) => setKits(kits.map((k, i) => i === index ? { ...k, fromLevelNumber: event.target.value === "" ? null : Number(event.target.value) } : k))} />
				<button type="button" className="wti-link" disabled={busy} onClick={() => setKits(kits.filter((_, i) => i !== index))}>Quitar</button>
			</div>)}
		{error ? <div className="wau-nota" style={{ marginTop: 12 }}>{error}</div> : null}
	</VentanaTienda>;
};

/**
 * Precios (Esteban 2026-10-30). Encargadas de precios (Karen y Christy en México, Lizzeth en Honduras): Precios por Aprobar, lo
 * que el equipo propuso. Super Admin y Contraloría: Precios por Revisar, todo lo que nadie ha revisado (por aprobar o ya aprobado
 * por una encargada). Ver, aprobar o dar por bueno, cambiar el precio o rechazar. Lo aprobado por una encargada ya está vigente.
 */
const PriceTray: React.FC<{ campusId: number; currency: string; review: boolean; onChanged: () => void }> = ({ campusId, currency, review, onChanged }) => {
	const http = HttpManager.getInstance().inventoryService;
	const [rows, setRows] = useState<any[] | null>(null);
	const [prices, setPrices] = useState<Record<number, string>>({});
	const [busyId, setBusyId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [rejecting, setRejecting] = useState<any | null>(null);
	const [rejectError, setRejectError] = useState<string | null>(null);
	// Del conteo llegan cientos de precios: se pueden aprobar (o dar por revisados) varios de una vez con su precio.
	const [selected, setSelected] = useState<number[]>([]);
	const [bulkBusy, setBulkBusy] = useState<boolean>(false);
	const load = useCallback(() => {
		(review ? http.priceReview(campusId) : http.priceRequests(campusId))
			.then((response) => { setRows(response.data); setPrices({}); setSelected([]); })
			.catch((requestError) => { setRows([]); setError(apiError(requestError, "No se pudo cargar la bandeja.")); });
	}, [campusId, review]);
	useEffect(() => { load(); }, [load]);
	const pct = (value: number | null) => value === null ? "-" : (value * 100).toFixed(1) + "%";
	const marginOf = (row: any, price: number) => row.cost === null || price <= 0 ? null : (price - row.cost) / price;
	/** The price each row stands for: what the approver set, else what was proposed. */
	const baseOf = (row: any): number => row.approvedPrice !== null && row.approvedPrice !== undefined ? Number(row.approvedPrice) : Number(row.suggestedPrice);
	const typedOf = (row: any): number => { const typed = prices[row.id]; return typed !== undefined && typed !== "" ? Number(typed) : baseOf(row); };
	const changed = (row: any): boolean => Math.abs(typedOf(row) - baseOf(row)) >= 0.005;
	const act = async (row: any) => {
		setBusyId(row.id);
		setError(null);
		try {
			if (review) await http.auditPrice(campusId, row.id, changed(row) ? "change" : "ok", changed(row) ? typedOf(row) : null, "");
			else await http.approvePrice(campusId, row.id, changed(row) ? typedOf(row) : null, "");
			load();
			onChanged();
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo guardar."));
			load();
		} finally {
			setBusyId(null);
		}
	};
	const actSelected = async () => {
		const edited = selected.filter((id) => { const row = (rows || []).find((entry) => entry.id === id); return row && changed(row); });
		if (edited.length > 0) { setError("Los que les cambiaste el precio guárdalos uno por uno (botón de su renglón)."); return; }
		setBulkBusy(true);
		setError(null);
		try {
			if (review) await http.auditPrices(campusId, selected);
			else await http.approvePrices(campusId, selected, "Aprobado en bloque");
			load();
			onChanged();
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudieron guardar."));
			load();
		} finally {
			setBulkBusy(false);
		}
	};
	if (rows === null) return <Loading />;
	const toggle = (id: number) => setSelected(selected.includes(id) ? selected.filter((entry) => entry !== id) : [...selected, id]);
	const allSelected = rows.length > 0 && selected.length === rows.length;
	return <>
		{error ? <div className="alert alert-danger" style={{ margin: "10px" }}>{error}</div> : null}
		<div style={{ ...ui.small, padding: "8px 12px" }}>{review
			? "Lo que las supervisoras aprobaron (o el equipo propuso) y nadie ha revisado. Lo aprobado ya está vigente; aquí se da por bueno, se cambia o se rechaza."
			: "Precios que propuso el equipo. Se venden en cuanto los apruebas; Super Admin y Contraloría los revisan después."}</div>
		{selected.length > 0 ? <div style={{ display: "flex", gap: "10px", alignItems: "center", padding: "10px 12px", borderBottom: "1px solid var(--wa-filo)", flexWrap: "wrap" }}>
			<b>{selected.length} seleccionado{selected.length === 1 ? "" : "s"}</b>
			<button type="button" className={ui.btnPrimary} disabled={bulkBusy || busyId !== null} onClick={actSelected}>{bulkBusy ? "Guardando…" : review ? "Marcar Revisados" : "Aprobar Seleccionados"}</button>
			<span style={ui.small}>Cada uno con su precio.</span>
			<button type="button" className="btn btn-link btn-sm" disabled={bulkBusy} onClick={() => setSelected([])}>Quitar Selección</button>
		</div> : null}
		{rows.length < 1 ? <Empty text={review ? "No hay precios por revisar." : "No hay precios por aprobar."} /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
			<thead><tr>
				<th style={{ ...ui.th, width: "34px" }}><input type="checkbox" checked={allSelected} disabled={bulkBusy} title="Seleccionar todos"
					onChange={() => setSelected(allSelected ? [] : rows.map((row) => row.id))} /></th>
				<th style={ui.th}>Producto</th>
				<th style={ui.th}>Capturó</th>
				{review ? <th style={ui.th}>Estado</th> : null}
				<th style={{ ...ui.th, ...ui.right }}>Precio vigente</th>
				<th style={{ ...ui.th, ...ui.right }}>Costo de compra</th>
				<th style={{ ...ui.th, width: "140px" }}>Precio</th>
				<th style={{ ...ui.th, ...ui.right }}>Margen bruto</th>
				<th style={{ ...ui.th, ...ui.right }}>Precio al objetivo</th>
				<th style={ui.th}></th>
			</tr></thead>
			<tbody>{rows.map((row) => {
				const typed = prices[row.id];
				const price = typedOf(row);
				const margin = marginOf(row, price);
				return <tr key={row.id}>
					<td style={ui.td}><input type="checkbox" checked={selected.includes(row.id)} disabled={bulkBusy} onChange={() => toggle(row.id)} /></td>
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.name}</div><div style={ui.tiny}>{row.waCode} · {row.kind}{row.isNew ? " · nuevo" : ""}</div>{row.note ? <div style={ui.small}>{row.note}</div> : null}</td>
					<td style={ui.td}>{row.requestedBy}<div style={ui.tiny}>{row.requestedAt}</div></td>
					{review ? <td style={ui.td}>{row.approvedBy
						? <><span style={{ color: "var(--wa-verde)", fontWeight: 600 }}>Aprobó {row.approvedBy}</span><div style={ui.tiny}>{row.approvedAt}</div></>
						: <span style={{ color: "var(--wa-oro)", fontWeight: 600 }}>Por aprobar</span>}</td> : null}
					<td style={{ ...ui.td, ...ui.right }}>{row.livePrice !== null && row.livePrice !== undefined ? currency + Number(row.livePrice).toFixed(2) : <span style={ui.small}>sin precio</span>}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.cost !== null ? currency + Number(row.cost).toFixed(2) : <span style={ui.small}>sin compra</span>}</td>
					<td style={ui.td}><input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={typed !== undefined ? typed : String(baseOf(row))}
						disabled={busyId !== null} onChange={(event) => setPrices({ ...prices, [row.id]: event.target.value })} /></td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700, color: margin === null ? "var(--wa-apagado)" : margin < Math.max(0.2, row.target || 0) ? "var(--wa-oro)" : "var(--wa-verde)" }}>{pct(margin)}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.priceForTarget !== null ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} disabled={busyId !== null}
						onClick={() => setPrices({ ...prices, [row.id]: String(row.priceForTarget) })}>{currency}{Number(row.priceForTarget).toFixed(2)}</button> : "-"}
						{row.target !== null ? <div style={ui.tiny}>objetivo {pct(row.target)}</div> : null}</td>
					<td style={ui.td}><div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "4px" }}>
						<button type="button" className={ui.btn} disabled={busyId !== null} onClick={() => { setRejectError(null); setRejecting(row); }}>Rechazar</button>
						<button type="button" className={ui.btnPrimary} disabled={busyId !== null || !(price > 0)} onClick={() => act(row)}>
							{busyId === row.id ? "…" : changed(row) ? "Cambiar Precio" : review ? "Revisado" : "Aprobar"}</button>
					</div></td>
				</tr>;
			})}</tbody>
		</table>}
		{rejecting ? <ReasonPrompt title={"Rechazar precio de " + rejecting.name} label="Motivo" confirmLabel="Rechazar" confirmClass="btn-primary"
			busy={busyId !== null} error={rejectError} onCancel={() => { if (busyId === null) setRejecting(null); }}
			onConfirm={async (reason) => {
				setBusyId(rejecting.id);
				setRejectError(null);
				try {
					if (review) await http.auditPrice(campusId, rejecting.id, "reject", null, reason);
					else await http.rejectPrice(campusId, rejecting.id, reason);
					setRejecting(null); load(); onChanged();
				}
				catch (requestError: any) { setRejectError(apiError(requestError, "No se pudo rechazar.")); }
				finally { setBusyId(null); }
			}} /> : null}
	</>;
};

export default Productos;
